import { Component, Prop, h, Build } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Footer component for SignNow brand site footer
 *
 * @product signnow
 * @version 1.0.2
 * @category footer
 * @contentConfig Footer component for site-wide footer with menus, legal links and newsletter
 * @constant FOOTER_FORM_DESCRIPTION - "Join our newsletter" - Newsletter form description
 * @constant FOOTER_FORM_SUBTITLE - "Stay updated with the latest features and insights from airSlate SignNow." - Newsletter form subtitle
 * @constant FOOTER_FORM_SUBMIT - "Start free trial" - Newsletter form submit button text
 * @constant FOOTER_FORM_NOTE - "No credit card required" - Form note text
 * @constant FOOTER_COPYRIGHT - "© 2026 airSlate SignNow. All rights reserved." - Copyright text
 * @constant FOOTER_MENU_FORMS - "Forms" - Forms menu section title
 * @constant FOOTER_MENU_PRICING - "Pricing" - Pricing menu section title
 * @constant FOOTER_MENU_SOLUTIONS - "Solutions" - Solutions menu section title
 * @constant FOOTER_MENU_RESOURCES - "Resources" - Resources menu section title
 * @constant FOOTER_MENU_COMPANY - "Company" - Company menu section title
 * @constant FOOTER_TOP_100_FORMS - "TOP-100 Forms" - Forms menu item
 * @constant FOOTER_FORMS_LIBRARY - "Forms Library" - Forms menu item
 * @constant FOOTER_IRS_LIBRARY - "IRS Library" - Forms menu item
 * @constant FOOTER_SAMPLES - "Samples" - Forms menu item
 * @constant FOOTER_TEMPLATES - "Templates" - Forms menu item
 * @constant FOOTER_ESIGNATURE_PLANS - "eSignature plans" - Pricing menu item
 * @constant FOOTER_API_PLANS - "API plans" - Pricing menu item
 * @constant FOOTER_CONTACT_SALES - "Contact sales" - Pricing menu item
 * @constant FOOTER_SMALL_BUSINESS - "Small Business" - Solutions menu item
 * @constant FOOTER_ENTERPRISE - "Enterprise" - Solutions menu item
 * @constant FOOTER_HEALTHCARE - "Healthcare" - Solutions menu item
 * @constant FOOTER_REAL_ESTATE - "Real Estate" - Solutions menu item
 * @constant FOOTER_LEGAL - "Legal" - Solutions menu item
 * @constant FOOTER_ESIGNATURE_SOLUTION - "eSignature solution" - Resources menu item
 * @constant FOOTER_FEATURES - "Features" - Resources menu item
 * @constant FOOTER_FUNCTIONS - "Functions" - Resources menu item
 * @constant FOOTER_INTEGRATIONS - "Integrations" - Resources menu item
 * @constant FOOTER_SOLUTIONS - "Solutions" - Resources menu item
 * @constant FOOTER_BUSINESS_CASES - "Business Cases" - Resources menu item
 * @constant FOOTER_CUSTOMER_STORIES - "Customer Stories" - Resources menu item
 * @constant FOOTER_PRODUCT_REVIEWS - "Product Reviews" - Resources menu item
 * @constant FOOTER_API - "API" - Resources menu item
 * @constant FOOTER_ESIGNATURE_LEGALITY - "eSignature Legality" - Resources menu item
 * @constant FOOTER_ALTERNATIVES - "Alternatives" - Resources menu item
 * @constant FOOTER_MENU_KNOWLEDGE_BASE - "Knowledge base" - Knowledge base menu section title
 * @constant FOOTER_RELEASE_NOTES - "Release notes" - Knowledge base menu item
 * @constant FOOTER_WEBINARS - "Webinars" - Knowledge base menu item
 * @constant FOOTER_HELP_CENTER - "Help Center" - Knowledge base menu item
 * @constant FOOTER_QA - "airSlate SignNow Q&A" - Knowledge base menu item
 * @constant FOOTER_HOW_TOS - "airSlate SignNow How-To's" - Knowledge base menu item
 * @constant FOOTER_ELECTRONIC_SIGNATURE - "Electronic Signature" - Knowledge base menu item
 * @constant FOOTER_SIGN_DOCUMENT - "How to sign a document electronically" - Knowledge base menu item
 * @constant FOOTER_SIGN_PDF - "How to eSign a PDF" - Knowledge base menu item
 * @constant FOOTER_CREATE_ESIGNATURE - "How to create eSignature" - Knowledge base menu item
 * @constant FOOTER_HANDWRITTEN_SIGNATURE - "Create a handwritten signature" - Knowledge base menu item
 * @constant FOOTER_MENU_PRODUCTS - "Products" - Products menu section title
 * @constant FOOTER_PDFFILLER - "pdfFiller" - Products menu item
 * @constant FOOTER_AIRSLATE_WORKFLOW - "airSlate WorkFlow" - Products menu item
 * @constant FOOTER_US_LEGAL_FORMS - "US Legal Forms" - Products menu item
 * @constant FOOTER_DOCHUB - "DocHub" - Products menu item
 * @constant FOOTER_INSTAPAGE - "Instapage" - Products menu item
 * @constant FOOTER_DOCUMENT_GENERATION - "Document generation" - Products menu item
 * @constant FOOTER_RPA - "Robotic process automation" - Products menu item
 * @constant FOOTER_CONTRACT_MANAGEMENT - "Contract management" - Products menu item
 * @constant FOOTER_WEB_FORMS - "Web forms" - Products menu item
 * @constant FOOTER_CONTRACT_NEGOTIATION - "Contract negotiation" - Products menu item
 * @constant FOOTER_AUTOMATE_WORKFLOWS - "Automate Workflows" - Products menu item
 * @constant FOOTER_ONLINE_SURVEYS - "Online Surveys" - Products menu item
 * @constant FOOTER_ACCEPT_PAYMENTS - "Accept Payments" - Products menu item
 * @constant FOOTER_PDF_EDITING - "PDF Editing" - Products menu item
 * @constant FOOTER_ABOUT_US - "About us" - Company menu item
 * @constant FOOTER_WHY_SIGNNOW - "Why airSlate SignNow?" - Company menu item
 * @constant FOOTER_SWITCH_SIGNNOW - "Switch to airSlate SignNow" - Company menu item
 * @constant FOOTER_LEGAL_HUB - "Legal Hub" - Company menu item
 * @constant FOOTER_TRUST_CENTER - "Trust center" - Company menu item
 * @constant FOOTER_LEADERSHIP - "Leadership" - Company menu item
 * @constant FOOTER_BRANDING_GUIDELINES - "Branding Guidelines" - Company menu item
 * @constant FOOTER_BECOME_PARTNER - "Become a partner" - Company menu item
 * @constant FOOTER_FIND_PARTNER - "Find a partner" - Company menu item
 * @constant FOOTER_AFFILIATE_PROGRAM - "Affiliate program" - Company menu item
 * @constant FOOTER_BLOG - "Blog" - Company menu item
 * @constant FOOTER_SUPPORT - "Support" - Company menu item
 * @constant FOOTER_MACHINE_TRANSLATION - "Machine Translation Disclaimer" - Company menu item
 * @constant FOOTER_BUG_BOUNTY - "Bug Bounty Program" - Company menu item
 * @constant FOOTER_PRIVACY_POLICY - "Privacy Notice" - Legal link
 * @constant FOOTER_TERMS_SERVICE - "Terms of Service" - Legal link
 * @constant FOOTER_SECURITY_AND_COMPLIANCE - "Security and compliance" - Legal link
 * @constant FOOTER_COOKIE_SETTINGS - "Cookie Settings" - Legal link
 * @constant LINK__FOOTER_ABOUT_US - "/about" - Link for "About us" menu item
 * @constant LINK__FOOTER_WHY_SIGNNOW - "/why-signnow" - Link for "Why airSlate SignNow?" menu item
 * @constant LINK__FOOTER_SWITCH_SIGNNOW - "/migrate" - Link for "Switch to airSlate SignNow" menu item
 * @constant LINK__FOOTER_LEGAL_HUB - "https://legal.signnow.com" - Link for "Legal Hub" menu item
 * @constant LINK__FOOTER_TRUST_CENTER - "/trust" - Link for "Trust center" menu item
 * @constant LINK__FOOTER_LEADERSHIP - "/leadership" - Link for "Leadership" menu item
 * @constant LINK__FOOTER_BRANDING_GUIDELINES - "/brand-guidelines" - Link for "Branding Guidelines" menu item
 * @constant LINK__FOOTER_BECOME_PARTNER - "/partnership" - Link for "Become a partner" menu item
 * @constant LINK__FOOTER_FIND_PARTNER - "/partners" - Link for "Find a partner" menu item
 * @constant LINK__FOOTER_AFFILIATE_PROGRAM - "https://www.airslate.com/affiliate" - Link for "Affiliate program" menu item
 * @constant LINK__FOOTER_BLOG - "/blog" - Link for "Blog" menu item
 * @constant LINK__FOOTER_SUPPORT - "https://support.signnow.com" - Link for "Support" menu item
 * @constant LINK__FOOTER_MACHINE_TRANSLATION - "https://legal.signnow.com/machine-translation-disclaimer" - Link for "Machine Translation Disclaimer" menu item
 * @constant LINK__FOOTER_BUG_BOUNTY - "/bug-bounty-policy" - Link for "Bug Bounty Program" menu item
 * @constant LINK__FOOTER_TOP_100_FORMS - "/top-100-forms" - Link for "TOP-100 Forms" menu item
 * @constant LINK__FOOTER_FORMS_LIBRARY - "/form-library" - Link for "Forms Library" menu item
 * @constant LINK__FOOTER_IRS_LIBRARY - "/irs-library" - Link for "IRS Library" menu item
 * @constant LINK__FOOTER_SAMPLES - "/samples" - Link for "Samples" menu item
 * @constant LINK__FOOTER_TEMPLATES - "/templates" - Link for "Templates" menu item
 * @constant LINK__FOOTER_ESIGNATURE_PLANS - "https://snseats.signnow.com/purchase/business_plans/pricing" - Link for "eSignature plans" menu item
 * @constant LINK__FOOTER_API_PLANS - "https://snseats.signnow.com/purchase/api/pricing" - Link for "API plans" menu item
 * @constant LINK__FOOTER_CONTACT_SALES - "/contact-sales" - Link for "Contact sales" menu item
 * @constant LINK__FOOTER_ESIGNATURE_SOLUTION - "/esignature" - Link for "eSignature solution" menu item
 * @constant LINK__FOOTER_FEATURES - "/features" - Link for "Features" menu item
 * @constant LINK__FOOTER_FUNCTIONS - "/functionality" - Link for "Functions" menu item
 * @constant LINK__FOOTER_INTEGRATIONS - "/integrations" - Link for "Integrations" menu item
 * @constant LINK__FOOTER_SOLUTIONS - "/solutions" - Link for "Solutions" menu item
 * @constant LINK__FOOTER_BUSINESS_CASES - "/business-cases" - Link for "Business Cases" menu item
 * @constant LINK__FOOTER_CUSTOMER_STORIES - "/customers" - Link for "Customer Stories" menu item
 * @constant LINK__FOOTER_PRODUCT_REVIEWS - "/g2-crowd-reviews" - Link for "Product Reviews" menu item
 * @constant LINK__FOOTER_API - "/api" - Link for "API" menu item
 * @constant LINK__FOOTER_ESIGNATURE_LEGALITY - "/legality" - Link for "eSignature Legality" menu item
 * @constant LINK__FOOTER_ALTERNATIVES - "/alternative" - Link for "Alternatives" menu item
 * @constant LINK__FOOTER_RELEASE_NOTES - "/release-notes" - Link for "Release notes" menu item
 * @constant LINK__FOOTER_WEBINARS - "/webinars" - Link for "Webinars" menu item
 * @constant LINK__FOOTER_HELP_CENTER - "/help" - Link for "Help Center" menu item
 * @constant LINK__FOOTER_QA - "/ask" - Link for "airSlate SignNow Q&A" menu item
 * @constant LINK__FOOTER_HOW_TOS - "/how-to" - Link for "airSlate SignNow How-To's" menu item
 * @constant LINK__FOOTER_ELECTRONIC_SIGNATURE - "/electronic-signature" - Link for "Electronic Signature" menu item
 * @constant LINK__FOOTER_SIGN_DOCUMENT - "/esignature/how-electronically-sign-document" - Link for "How to sign a document electronically" menu item
 * @constant LINK__FOOTER_SIGN_PDF - "/esignature/how-to-esign-a-pdf" - Link for "How to eSign a PDF" menu item
 * @constant LINK__FOOTER_CREATE_ESIGNATURE - "/esignature/how-to-create-esignature" - Link for "How to create eSignature" menu item
 * @constant LINK__FOOTER_HANDWRITTEN_SIGNATURE - "/esignature/create-a-handwritten-signature" - Link for "Create a handwritten signature" menu item
 * @constant LINK__FOOTER_PDFFILLER - "https://www.pdffiller.com" - Link for "pdfFiller" menu item
 * @constant LINK__FOOTER_AIRSLATE_WORKFLOW - "https://www.airslate.com/workflows?utm_source=signnow&utm_medium=footer" - Link for "airSlate WorkFlow" menu item
 * @constant LINK__FOOTER_US_LEGAL_FORMS - "https://www.uslegalforms.com" - Link for "US Legal Forms" menu item
 * @constant LINK__FOOTER_DOCHUB - "https://www.dochub.com" - Link for "DocHub" menu item
 * @constant LINK__FOOTER_INSTAPAGE - "https://instapage.com" - Link for "Instapage" menu item
 * @constant LINK__FOOTER_DOCUMENT_GENERATION - "/product/document-generation" - Link for "Document generation" menu item
 * @constant LINK__FOOTER_RPA - "/product/robotic-process-automation" - Link for "Robotic process automation" menu item
 * @constant LINK__FOOTER_CONTRACT_MANAGEMENT - "/product/contract-management" - Link for "Contract management" menu item
 * @constant LINK__FOOTER_WEB_FORMS - "/product/web-forms" - Link for "Web forms" menu item
 * @constant LINK__FOOTER_CONTRACT_NEGOTIATION - "/product/contract-negotiation" - Link for "Contract negotiation" menu item
 * @constant LINK__FOOTER_AUTOMATE_WORKFLOWS - "/product/create-and-automate-workflows" - Link for "Automate Workflows" menu item
 * @constant LINK__FOOTER_ONLINE_SURVEYS - "/product/online-surveys" - Link for "Online Surveys" menu item
 * @constant LINK__FOOTER_ACCEPT_PAYMENTS - "/product/payment-forms" - Link for "Accept Payments" menu item
 * @constant LINK__FOOTER_PDF_EDITING - "/product/pdf-editing" - Link for "PDF Editing" menu item
 * @constant LINK__FOOTER_PRIVACY_POLICY - "/privacy_notice" - Link for "Privacy Policy" legal link
 * @constant LINK__FOOTER_TERMS_SERVICE - "/terms-of-service" - Link for "Terms of Service" legal link
 * @constant LINK__FOOTER_SECURITY_AND_COMPLIANCE - "/security" - Link for "Security and compliance" legal link
 * @slot form-description - {required} - Newsletter subscription form description
 * @slot form - {required} - Newsletter subscription form
 * @slot logo - {required} - Company logo
 * @slot menus - {required} - Footer navigation menus
 * @slot copyright - {required} - Copyright information
 * @slot legal - {required} - Legal links (privacy, terms)
 * @slot socials - {required} - Social media links
 * @slot language-switcher - {required}, <me-language-switcher>
 */
@Component({
  tag: 'sn-footer-type-5',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFooterType5 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  /**
   * Render SEO-friendly footer for server-side rendering
   * Contains all important links in simple HTML structure for search engine indexing
   * This version is rendered only on server, full interactive component renders on client
   */
  private renderSeoVersion() {
    return (
      <footer class="footer-type-5 footer-type-5--white" role="contentinfo" aria-label="Site footer" itemScope itemType="https://schema.org/Organization">
        <div class="footer-type-5__logo" itemProp="logo" style={{ visibility: 'hidden' }}>
          <a href="/" aria-label="Home">
            <img src="https://marketing-static.signnow.com/310/images/global/svg/signNow-logo-Colored.svg" alt="airSlate signNow logo" loading="lazy" />
          </a>
        </div>
        <div class="footer-type-5__menus" style={{ visibility: 'hidden' }}>
          <nav>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ABOUT_US', '/about')}>{getConstant(this.constants, 'FOOTER_ABOUT_US', 'About us')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_WHY_SIGNNOW', '/why-signnow')}>{getConstant(this.constants, 'FOOTER_WHY_SIGNNOW', 'Why airSlate SignNow?')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_SWITCH_SIGNNOW', '/migrate')}>
              {getConstant(this.constants, 'FOOTER_SWITCH_SIGNNOW', 'Switch to airSlate SignNow')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_LEGAL_HUB', 'https://legal.signnow.com')}>{getConstant(this.constants, 'FOOTER_LEGAL_HUB', 'Legal Hub')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_TRUST_CENTER', '/trust')}>{getConstant(this.constants, 'FOOTER_TRUST_CENTER', 'Trust center')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_LEADERSHIP', '/leadership')}>{getConstant(this.constants, 'FOOTER_LEADERSHIP', 'Leadership')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_BRANDING_GUIDELINES', '/brand-guidelines')}>
              {getConstant(this.constants, 'FOOTER_BRANDING_GUIDELINES', 'Branding Guidelines')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_BECOME_PARTNER', '/partnership')}>{getConstant(this.constants, 'FOOTER_BECOME_PARTNER', 'Become a partner')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_FIND_PARTNER', '/partners')}>{getConstant(this.constants, 'FOOTER_FIND_PARTNER', 'Find a partner')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_AFFILIATE_PROGRAM', 'https://www.airslate.com/affiliate')}>
              {getConstant(this.constants, 'FOOTER_AFFILIATE_PROGRAM', 'Affiliate program')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_BLOG', '/blog')}>{getConstant(this.constants, 'FOOTER_BLOG', 'Blog')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_SUPPORT', 'https://support.signnow.com')}>{getConstant(this.constants, 'FOOTER_SUPPORT', 'Support')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_MACHINE_TRANSLATION', 'https://legal.signnow.com/machine-translation-disclaimer')}>
              {getConstant(this.constants, 'FOOTER_MACHINE_TRANSLATION', 'Machine Translation Disclaimer')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_BUG_BOUNTY', '/bug-bounty-policy')}>{getConstant(this.constants, 'FOOTER_BUG_BOUNTY', 'Bug Bounty Program')}</a>
          </nav>
          <nav>
            <a href={getConstant(this.constants, 'LINK__FOOTER_TOP_100_FORMS', '/top-100-forms')}>{getConstant(this.constants, 'FOOTER_TOP_100_FORMS', 'TOP-100 Forms')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_FORMS_LIBRARY', '/form-library')}>{getConstant(this.constants, 'FOOTER_FORMS_LIBRARY', 'Forms Library')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_IRS_LIBRARY', '/irs-library')}>{getConstant(this.constants, 'FOOTER_IRS_LIBRARY', 'IRS Library')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_SAMPLES', '/samples')}>{getConstant(this.constants, 'FOOTER_SAMPLES', 'Samples')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_TEMPLATES', '/templates')}>{getConstant(this.constants, 'FOOTER_TEMPLATES', 'Templates')}</a>
          </nav>
          <nav>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ESIGNATURE_PLANS', 'https://snseats.signnow.com/purchase/business_plans/pricing')}>
              {getConstant(this.constants, 'FOOTER_ESIGNATURE_PLANS', 'eSignature plans')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_API_PLANS', 'https://snseats.signnow.com/purchase/api/pricing')}>
              {getConstant(this.constants, 'FOOTER_API_PLANS', 'API plans')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_CONTACT_SALES', '/contact-sales')}>{getConstant(this.constants, 'FOOTER_CONTACT_SALES', 'Contact sales')}</a>
          </nav>
          <nav>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ESIGNATURE_SOLUTION', '/esignature')}>
              {getConstant(this.constants, 'FOOTER_ESIGNATURE_SOLUTION', 'eSignature solution')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_FEATURES', '/features')}>{getConstant(this.constants, 'FOOTER_FEATURES', 'Features')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_FUNCTIONS', '/functionality')}>{getConstant(this.constants, 'FOOTER_FUNCTIONS', 'Functions')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_INTEGRATIONS', '/integrations')}>{getConstant(this.constants, 'FOOTER_INTEGRATIONS', 'Integrations')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_SOLUTIONS', '/solutions')}>{getConstant(this.constants, 'FOOTER_SOLUTIONS', 'Solutions')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_BUSINESS_CASES', '/business-cases')}>{getConstant(this.constants, 'FOOTER_BUSINESS_CASES', 'Business Cases')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_CUSTOMER_STORIES', '/customers')}>{getConstant(this.constants, 'FOOTER_CUSTOMER_STORIES', 'Customer Stories')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_PRODUCT_REVIEWS', '/g2-crowd-reviews')}>
              {getConstant(this.constants, 'FOOTER_PRODUCT_REVIEWS', 'Product Reviews')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_API', '/api')}>{getConstant(this.constants, 'FOOTER_API', 'API')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ESIGNATURE_LEGALITY', '/legality')}>
              {getConstant(this.constants, 'FOOTER_ESIGNATURE_LEGALITY', 'eSignature Legality')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ALTERNATIVES', '/alternative')}>{getConstant(this.constants, 'FOOTER_ALTERNATIVES', 'Alternatives')}</a>
          </nav>
          <nav>
            <a href={getConstant(this.constants, 'LINK__FOOTER_RELEASE_NOTES', '/release-notes')}>{getConstant(this.constants, 'FOOTER_RELEASE_NOTES', 'Release notes')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_WEBINARS', '/webinars')}>{getConstant(this.constants, 'FOOTER_WEBINARS', 'Webinars')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_HELP_CENTER', '/help')}>{getConstant(this.constants, 'FOOTER_HELP_CENTER', 'Help Center')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_QA', '/ask')}>{getConstant(this.constants, 'FOOTER_QA', 'airSlate SignNow Q&A')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_HOW_TOS', '/how-to')}>{getConstant(this.constants, 'FOOTER_HOW_TOS', "airSlate SignNow How-To's")}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ELECTRONIC_SIGNATURE', '/electronic-signature')}>
              {getConstant(this.constants, 'FOOTER_ELECTRONIC_SIGNATURE', 'Electronic Signature')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_SIGN_DOCUMENT', '/esignature/how-electronically-sign-document')}>
              {getConstant(this.constants, 'FOOTER_SIGN_DOCUMENT', 'How to sign a document electronically')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_SIGN_PDF', '/esignature/how-to-esign-a-pdf')}>
              {getConstant(this.constants, 'FOOTER_SIGN_PDF', 'How to eSign a PDF')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_CREATE_ESIGNATURE', '/esignature/how-to-create-esignature')}>
              {getConstant(this.constants, 'FOOTER_CREATE_ESIGNATURE', 'How to create eSignature')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_HANDWRITTEN_SIGNATURE', '/esignature/create-a-handwritten-signature')}>
              {getConstant(this.constants, 'FOOTER_HANDWRITTEN_SIGNATURE', 'Create a handwritten signature')}
            </a>
          </nav>
          <nav>
            <a href={getConstant(this.constants, 'LINK__FOOTER_PDFFILLER', 'https://www.pdffiller.com')}>{getConstant(this.constants, 'FOOTER_PDFFILLER', 'pdfFiller')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_AIRSLATE_WORKFLOW', 'https://www.airslate.com/workflows?utm_source=signnow&utm_medium=footer')}>
              {getConstant(this.constants, 'FOOTER_AIRSLATE_WORKFLOW', 'airSlate WorkFlow')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_US_LEGAL_FORMS', 'https://www.uslegalforms.com')}>
              {getConstant(this.constants, 'FOOTER_US_LEGAL_FORMS', 'US Legal Forms')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_DOCHUB', 'https://www.dochub.com')}>{getConstant(this.constants, 'FOOTER_DOCHUB', 'DocHub')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_INSTAPAGE', 'https://instapage.com')}>{getConstant(this.constants, 'FOOTER_INSTAPAGE', 'Instapage')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_DOCUMENT_GENERATION', '/product/document-generation')}>
              {getConstant(this.constants, 'FOOTER_DOCUMENT_GENERATION', 'Document generation')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_RPA', '/product/robotic-process-automation')}>
              {getConstant(this.constants, 'FOOTER_RPA', 'Robotic process automation')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_CONTRACT_MANAGEMENT', '/product/contract-management')}>
              {getConstant(this.constants, 'FOOTER_CONTRACT_MANAGEMENT', 'Contract management')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_WEB_FORMS', '/product/web-forms')}>{getConstant(this.constants, 'FOOTER_WEB_FORMS', 'Web forms')}</a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_CONTRACT_NEGOTIATION', '/product/contract-negotiation')}>
              {getConstant(this.constants, 'FOOTER_CONTRACT_NEGOTIATION', 'Contract negotiation')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_AUTOMATE_WORKFLOWS', '/product/create-and-automate-workflows')}>
              {getConstant(this.constants, 'FOOTER_AUTOMATE_WORKFLOWS', 'Automate Workflows')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ONLINE_SURVEYS', '/product/online-surveys')}>
              {getConstant(this.constants, 'FOOTER_ONLINE_SURVEYS', 'Online Surveys')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_ACCEPT_PAYMENTS', '/product/payment-forms')}>
              {getConstant(this.constants, 'FOOTER_ACCEPT_PAYMENTS', 'Accept Payments')}
            </a>
            <a href={getConstant(this.constants, 'LINK__FOOTER_PDF_EDITING', '/product/pdf-editing')}>{getConstant(this.constants, 'FOOTER_PDF_EDITING', 'PDF Editing')}</a>
          </nav>
        </div>
        <div class="footer-type-5__copyright" style={{ visibility: 'hidden' }}>
          <p>{getConstant(this.constants, 'FOOTER_COPYRIGHT', '© 2026 airSlate SignNow. All rights reserved.')}</p>
        </div>
        <div class="footer-type-5__legal-links" style={{ visibility: 'hidden' }}>
          <a href={getConstant(this.constants, 'LINK__FOOTER_PRIVACY_POLICY', '/privacy_notice')}>{getConstant(this.constants, 'FOOTER_PRIVACY_POLICY', 'Privacy Notice')}</a>
          <a href={getConstant(this.constants, 'LINK__FOOTER_TERMS_SERVICE', '/terms-of-service')}>{getConstant(this.constants, 'FOOTER_TERMS_SERVICE', 'Terms of Service')}</a>
          <a href={getConstant(this.constants, 'LINK__FOOTER_SECURITY_AND_COMPLIANCE', '/security')}>
            {getConstant(this.constants, 'FOOTER_SECURITY_AND_COMPLIANCE', 'Security and compliance')}
          </a>
        </div>
        <nav style={{ visibility: 'hidden' }}>
          <a href="https://www.linkedin.com/company/signnow-esignature">LinkedIn</a>
          <a href="https://x.com/signnow">X (Twitter)</a>
          <a href="https://www.facebook.com/esignaturesignnow">Facebook</a>
          <a href="https://www.youtube.com/channel/UCap5EPWIJNTDtNQEofyg0Hg">YouTube</a>
        </nav>
      </footer>
    );
  }

  render() {
    // Render SEO-friendly version on server, full interactive component on client
    if (!Build.isBrowser) {
      return this.renderSeoVersion();
    }
    return (
      <footer-type-5>
        <div slot="form-description" class="footer-apps-list">
          <div class="footer-apps-list__item">
            <a
              class="footer-apps-list__link click-event"
              data-event-name="click|touchstart|keypress"
              data-event-url="/event/events-track"
              data-click-event-type="sn_footer_google_play_market_click"
              href="https://play.google.com/store/apps/details?id=com.signnow.android&referrer=utm_source=main_site_footer"
              title="Google Play"
              target="_blank"
              rel="nofollow"
            >
              <me-icon name="https://marketing-static.signnow.com/347/images/me/footer-icons/google-play.svg" />
            </a>
            <div class="footer-apps-list__info">
              <div class="footer-apps-list__star">
                <me-icon name="https://marketing-static.signnow.com/347/images/me/footer-icons/star-icon.svg" />
              </div>
              <span class="footer-apps-list__star-value">4,6</span>
              <span class="footer-apps-list__ratings">5.86K Ratings</span>
            </div>
          </div>
          <div class="footer-apps-list__item">
            <a
              class="footer-apps-list__link click-event"
              data-event-name="click|touchstart|keypress"
              data-event-url="/event/events-track"
              data-click-event-type="sn_footer_apple_app_store_click"
              href="https://apps.apple.com/app/apple-store/id489262811?pt=482524&ct=main_site_footer&mt=8"
              title="App Store"
              target="_blank"
              rel="nofollow"
            >
              <me-icon name="https://marketing-static.signnow.com/347/images/me/footer-icons/app-store.svg" />
            </a>
            <div class="footer-apps-list__info">
              <div class="footer-apps-list__star">
                <me-icon name="https://marketing-static.signnow.com/347/images/me/footer-icons/star-icon.svg" />
              </div>
              <span class="footer-apps-list__star-value">4,7</span>
              <span class="footer-apps-list__ratings">9.7K Ratings</span>
            </div>
          </div>
        </div>
        <form-free-trial-type-1
          slot="form"
          button-variant="primary"
          mobile-layout="stacked"
          note={getConstant(this.constants, 'FOOTER_FORM_NOTE', 'No credit card required')}
          submitText={getConstant(this.constants, 'FOOTER_FORM_SUBMIT', 'Start free trial')}
          constants={this.constants as Record<string, string>}
        />
        <me-logo slot="logo" href="/" src="https://marketing-static.signnow.com/310/images/global/svg/signNow-logo-Colored.svg" alt="airSlate signNow logo" />

        <footer-menu-type-1 slot="menus">
          <me-text slot="title" variant="regular" weight="semi-bold">
            {getConstant(this.constants, 'FOOTER_MENU_COMPANY', 'Company')}
          </me-text>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_ABOUT_US', '/about')}>
            {getConstant(this.constants, 'FOOTER_ABOUT_US', 'About us')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_WHY_SIGNNOW', '/why-signnow')}>
            {getConstant(this.constants, 'FOOTER_WHY_SIGNNOW', 'Why airSlate SignNow?')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_SWITCH_SIGNNOW', '/migrate')}>
            {getConstant(this.constants, 'FOOTER_SWITCH_SIGNNOW', 'Switch to airSlate SignNow')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_LEGAL_HUB', 'https://legal.signnow.com')} target="_blank">
            {getConstant(this.constants, 'FOOTER_LEGAL_HUB', 'Legal Hub')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_TRUST_CENTER', '/trust')}>
            {getConstant(this.constants, 'FOOTER_TRUST_CENTER', 'Trust center')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_LEADERSHIP', '/leadership')}>
            {getConstant(this.constants, 'FOOTER_LEADERSHIP', 'Leadership')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_BRANDING_GUIDELINES', '/brand-guidelines')}>
            {getConstant(this.constants, 'FOOTER_BRANDING_GUIDELINES', 'Branding Guidelines')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_BECOME_PARTNER', '/partnership')}>
            {getConstant(this.constants, 'FOOTER_BECOME_PARTNER', 'Become a partner')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_FIND_PARTNER', '/partners')}>
            {getConstant(this.constants, 'FOOTER_FIND_PARTNER', 'Find a partner')}
          </me-button>
          <me-button
            slot="items"
            tag="a"
            variant="ghost"
            href={getConstant(this.constants, 'LINK__FOOTER_AFFILIATE_PROGRAM', 'https://www.airslate.com/affiliate')}
            target="_blank"
          >
            {getConstant(this.constants, 'FOOTER_AFFILIATE_PROGRAM', 'Affiliate program')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_BLOG', '/blog')} target="_blank">
            {getConstant(this.constants, 'FOOTER_BLOG', 'Blog')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_SUPPORT', 'https://support.signnow.com')} rel="nofollow" target="_blank">
            {getConstant(this.constants, 'FOOTER_SUPPORT', 'Support')}
          </me-button>
          <me-button
            slot="items"
            tag="a"
            variant="ghost"
            href={getConstant(this.constants, 'LINK__FOOTER_MACHINE_TRANSLATION', 'https://legal.signnow.com/machine-translation-disclaimer')}
          >
            {getConstant(this.constants, 'FOOTER_MACHINE_TRANSLATION', 'Machine Translation Disclaimer')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_BUG_BOUNTY', '/bug-bounty-policy')}>
            {getConstant(this.constants, 'FOOTER_BUG_BOUNTY', 'Bug Bounty Program')}
          </me-button>
        </footer-menu-type-1>

        <div slot="menus">
          <footer-menu-type-1>
            <me-text slot="title" variant="regular" weight="semi-bold">
              {getConstant(this.constants, 'FOOTER_MENU_FORMS', 'Forms')}
            </me-text>
            <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_TOP_100_FORMS', '/top-100-forms')}>
              {getConstant(this.constants, 'FOOTER_TOP_100_FORMS', 'TOP-100 Forms')}
            </me-button>
            <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_FORMS_LIBRARY', '/form-library')}>
              {getConstant(this.constants, 'FOOTER_FORMS_LIBRARY', 'Forms Library')}
            </me-button>
            <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_IRS_LIBRARY', '/irs-library')}>
              {getConstant(this.constants, 'FOOTER_IRS_LIBRARY', 'IRS Library')}
            </me-button>
            <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_SAMPLES', '/samples')}>
              {getConstant(this.constants, 'FOOTER_SAMPLES', 'Samples')}
            </me-button>
            <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_TEMPLATES', '/templates')}>
              {getConstant(this.constants, 'FOOTER_TEMPLATES', 'Templates')}
            </me-button>
          </footer-menu-type-1>

          <footer-menu-type-1>
            <me-text slot="title" variant="regular" weight="semi-bold">
              {getConstant(this.constants, 'FOOTER_MENU_PRICING', 'Pricing')}
            </me-text>
            <me-button
              slot="items"
              tag="a"
              variant="ghost"
              href={getConstant(this.constants, 'LINK__FOOTER_ESIGNATURE_PLANS', 'https://snseats.signnow.com/purchase/business_plans/pricing')}
            >
              {getConstant(this.constants, 'FOOTER_ESIGNATURE_PLANS', 'eSignature plans')}
            </me-button>
            <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_API_PLANS', 'https://snseats.signnow.com/purchase/api/pricing')}>
              {getConstant(this.constants, 'FOOTER_API_PLANS', 'API plans')}
            </me-button>
            <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_CONTACT_SALES', '/contact-sales')}>
              {getConstant(this.constants, 'FOOTER_CONTACT_SALES', 'Contact sales')}
            </me-button>
          </footer-menu-type-1>
        </div>

        <footer-menu-type-1 slot="menus">
          <me-text slot="title" variant="regular" weight="semi-bold">
            {getConstant(this.constants, 'FOOTER_MENU_RESOURCES', 'Resources')}
          </me-text>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_ESIGNATURE_SOLUTION', '/esignature')}>
            {getConstant(this.constants, 'FOOTER_ESIGNATURE_SOLUTION', 'eSignature solution')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_FEATURES', '/features')}>
            {getConstant(this.constants, 'FOOTER_FEATURES', 'Features')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_FUNCTIONS', '/functionality')}>
            {getConstant(this.constants, 'FOOTER_FUNCTIONS', 'Functions')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_INTEGRATIONS', '/integrations')}>
            {getConstant(this.constants, 'FOOTER_INTEGRATIONS', 'Integrations')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_SOLUTIONS', '/solutions')}>
            {getConstant(this.constants, 'FOOTER_SOLUTIONS', 'Solutions')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_BUSINESS_CASES', '/business-cases')}>
            {getConstant(this.constants, 'FOOTER_BUSINESS_CASES', 'Business Cases')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_CUSTOMER_STORIES', '/customers')}>
            {getConstant(this.constants, 'FOOTER_CUSTOMER_STORIES', 'Customer Stories')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_PRODUCT_REVIEWS', '/g2-crowd-reviews')}>
            {getConstant(this.constants, 'FOOTER_PRODUCT_REVIEWS', 'Product Reviews')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_API', '/api')}>
            {getConstant(this.constants, 'FOOTER_API', 'API')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_ESIGNATURE_LEGALITY', '/legality')}>
            {getConstant(this.constants, 'FOOTER_ESIGNATURE_LEGALITY', 'eSignature Legality')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_ALTERNATIVES', '/alternative')}>
            {getConstant(this.constants, 'FOOTER_ALTERNATIVES', 'Alternatives')}
          </me-button>
        </footer-menu-type-1>

        <footer-menu-type-1 slot="menus">
          <me-text slot="title" variant="regular" weight="semi-bold">
            {getConstant(this.constants, 'FOOTER_MENU_KNOWLEDGE_BASE', 'Knowledge base')}
          </me-text>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_RELEASE_NOTES', '/release-notes')}>
            {getConstant(this.constants, 'FOOTER_RELEASE_NOTES', 'Release notes')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_WEBINARS', '/webinars')}>
            {getConstant(this.constants, 'FOOTER_WEBINARS', 'Webinars')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_HELP_CENTER', '/help')}>
            {getConstant(this.constants, 'FOOTER_HELP_CENTER', 'Help Center')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_QA', '/ask')}>
            {getConstant(this.constants, 'FOOTER_QA', 'airSlate SignNow Q&A')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_HOW_TOS', '/how-to')}>
            {getConstant(this.constants, 'FOOTER_HOW_TOS', "airSlate SignNow How-To's")}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_ELECTRONIC_SIGNATURE', '/electronic-signature')}>
            {getConstant(this.constants, 'FOOTER_ELECTRONIC_SIGNATURE', 'Electronic Signature')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_SIGN_DOCUMENT', '/esignature/how-electronically-sign-document')}>
            {getConstant(this.constants, 'FOOTER_SIGN_DOCUMENT', 'How to sign a document electronically')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_SIGN_PDF', '/esignature/how-to-esign-a-pdf')}>
            {getConstant(this.constants, 'FOOTER_SIGN_PDF', 'How to eSign a PDF')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_CREATE_ESIGNATURE', '/esignature/how-to-create-esignature')}>
            {getConstant(this.constants, 'FOOTER_CREATE_ESIGNATURE', 'How to create eSignature')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_HANDWRITTEN_SIGNATURE', '/esignature/create-a-handwritten-signature')}>
            {getConstant(this.constants, 'FOOTER_HANDWRITTEN_SIGNATURE', 'Create a handwritten signature')}
          </me-button>
        </footer-menu-type-1>

        <footer-menu-type-1 slot="menus">
          <me-text slot="title" variant="regular" weight="semi-bold">
            {getConstant(this.constants, 'FOOTER_MENU_PRODUCTS', 'Products')}
          </me-text>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_PDFFILLER', 'https://www.pdffiller.com')} target="_blank">
            {getConstant(this.constants, 'FOOTER_PDFFILLER', 'pdfFiller')}
          </me-button>
          <me-button
            slot="items"
            tag="a"
            variant="ghost"
            href={getConstant(this.constants, 'LINK__FOOTER_AIRSLATE_WORKFLOW', 'https://www.airslate.com/workflows?utm_source=signnow&utm_medium=footer')}
            target="_blank"
          >
            {getConstant(this.constants, 'FOOTER_AIRSLATE_WORKFLOW', 'airSlate WorkFlow')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_US_LEGAL_FORMS', 'https://www.uslegalforms.com')} target="_blank">
            {getConstant(this.constants, 'FOOTER_US_LEGAL_FORMS', 'US Legal Forms')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_DOCHUB', 'https://www.dochub.com')} target="_blank">
            {getConstant(this.constants, 'FOOTER_DOCHUB', 'DocHub')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_INSTAPAGE', 'https://instapage.com')} target="_blank">
            {getConstant(this.constants, 'FOOTER_INSTAPAGE', 'Instapage')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_DOCUMENT_GENERATION', '/product/document-generation')}>
            {getConstant(this.constants, 'FOOTER_DOCUMENT_GENERATION', 'Document generation')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_RPA', '/product/robotic-process-automation')}>
            {getConstant(this.constants, 'FOOTER_RPA', 'Robotic process automation')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_CONTRACT_MANAGEMENT', '/product/contract-management')}>
            {getConstant(this.constants, 'FOOTER_CONTRACT_MANAGEMENT', 'Contract management')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_WEB_FORMS', '/product/web-forms')}>
            {getConstant(this.constants, 'FOOTER_WEB_FORMS', 'Web forms')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_CONTRACT_NEGOTIATION', '/product/contract-negotiation')}>
            {getConstant(this.constants, 'FOOTER_CONTRACT_NEGOTIATION', 'Contract negotiation')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_AUTOMATE_WORKFLOWS', '/product/create-and-automate-workflows')}>
            {getConstant(this.constants, 'FOOTER_AUTOMATE_WORKFLOWS', 'Automate Workflows')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_ONLINE_SURVEYS', '/product/online-surveys')}>
            {getConstant(this.constants, 'FOOTER_ONLINE_SURVEYS', 'Online Surveys')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_ACCEPT_PAYMENTS', '/product/payment-forms')}>
            {getConstant(this.constants, 'FOOTER_ACCEPT_PAYMENTS', 'Accept Payments')}
          </me-button>
          <me-button slot="items" tag="a" variant="ghost" href={getConstant(this.constants, 'LINK__FOOTER_PDF_EDITING', '/product/pdf-editing')}>
            {getConstant(this.constants, 'FOOTER_PDF_EDITING', 'PDF Editing')}
          </me-button>
        </footer-menu-type-1>

        <me-column slot="copyright">
          <me-text variant="small">{getConstant(this.constants, 'FOOTER_COPYRIGHT', '© 2026 airSlate SignNow. All rights reserved.')}</me-text>
        </me-column>

        <me-column slot="legal" class="footer-type-5__legal-links">
          <me-button tag="a" href={getConstant(this.constants, 'LINK__FOOTER_PRIVACY_POLICY', '/privacy_notice')} target="_blank" variant="ghost">
            {getConstant(this.constants, 'FOOTER_PRIVACY_POLICY', 'Privacy Notice')}
          </me-button>
          <me-button tag="a" href={getConstant(this.constants, 'LINK__FOOTER_TERMS_SERVICE', '/terms-of-service')} target="_blank" variant="ghost">
            {getConstant(this.constants, 'FOOTER_TERMS_SERVICE', 'Terms of Service')}
          </me-button>
          <me-button tag="a" href={getConstant(this.constants, 'LINK__FOOTER_SECURITY_AND_COMPLIANCE', '/security')} target="_blank" variant="ghost">
            {getConstant(this.constants, 'FOOTER_SECURITY_AND_COMPLIANCE', 'Security and Compliance')}
          </me-button>
        </me-column>

        <me-socials
          slot="socials"
          items={[
            { type: 'linkedIn', href: 'https://www.linkedin.com/company/signnow-esignature', label: 'LinkedIn' },
            { type: 'x', href: 'https://x.com/signnow', label: 'X (Twitter)' },
            { type: 'facebook', href: 'https://www.facebook.com/esignaturesignnow', label: 'Facebook' },
            { type: 'youtube', href: 'https://www.youtube.com/channel/UCap5EPWIJNTDtNQEofyg0Hg', label: 'YouTube' },
          ]}
        />
        <slot name="language-switcher"></slot>
      </footer-type-5>
    );
  }
}
